import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, euro, dataIt, STATI_PREVENTIVO } from "@/lib/api";
import { PrintHeader, PrintFooter } from "@/components/PrintHeader";
import { FirmaCliente } from "@/components/FirmaCliente";
import { CondividiButton } from "@/components/CondividiButton";
import { InviaPdfButton } from "@/components/InviaPdfButton";
import { scadenza } from "@/pages/Preventivi";

const Th = ({ children, right }) => <th className={`border-b-2 border-slate-900 py-1.5 text-[10px] font-bold uppercase tracking-wider ${right ? "text-right" : "text-left"}`}>{children}</th>;
const Td = ({ children, right, mono }) => <td className={`border-b border-slate-200 py-1.5 ${right ? "text-right" : ""} ${mono ? "font-mono" : ""}`}>{children}</td>;
const Box = ({ title, children }) => (
  <div className="rounded border border-slate-300 p-3"><p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">{title}</p>{children}</div>
);

export default function PreventivoStampa({ pubblico }) {
  const params = useParams();
  const id = pubblico?.doc.id || params.id;
  const navigate = useNavigate();
  const [p, setP] = useState(pubblico?.doc || null);
  const [neg, setNeg] = useState(pubblico?.neg || {});

  const load = () => api.get(`/preventivi/${id}`).then((r) => setP(r.data));
  useEffect(() => {
    if (pubblico) return;
    load();
    api.get("/impostazioni").then((r) => setNeg(r.data));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!p) return <div className="py-20 text-center text-sm text-slate-500">Caricamento...</div>;
  const c = p.cliente || {}, b = p.bici || {};

  return (
    <div className="min-h-screen bg-slate-100 py-8 print:bg-white print:py-0">
      <div className="mx-auto mb-4 flex max-w-[210mm] flex-wrap justify-between gap-2 px-4 print:hidden">
        {!pubblico && <Button variant="outline" onClick={() => navigate(`/preventivi/${id}`)} data-testid="prev-print-back-button"><ArrowLeft className="mr-1 h-4 w-4" /> Torna al preventivo</Button>}
        <div className="ml-auto flex flex-wrap justify-end gap-2">
          {!pubblico && <InviaPdfButton p={p} negozio={neg.nome_negozio} />}
          {!pubblico && <CondividiButton tipo="preventivo" id={id} telefono={c.telefono} nome={c.nome} numero={p.numero} firmato={!!p.firma_cliente} negozio={neg.nome_negozio} />}
          {pubblico && <Button variant="outline" onClick={() => { window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/pubblico/${pubblico.token}/pdf`; }} data-testid="prev-scarica-pdf-button">Scarica PDF</Button>}
          <Button onClick={() => window.print()} className="bg-orange-600 hover:bg-orange-700" data-testid="prev-print-now-button"><Printer className="mr-1 h-4 w-4" /> {pubblico ? "Stampa / Salva PDF" : "Stampa"}</Button>
        </div>
      </div>
      <div className="print-sheet mx-auto max-w-[210mm] bg-white p-10 text-[12px] text-slate-900 shadow-md print:max-w-none print:p-0 print:shadow-none" data-testid="prev-print-sheet">
        <PrintHeader neg={neg} titolo={neg.titolo_preventivo || "Preventivo di riparazione"}>
          <p className="font-mono text-2xl font-bold" data-testid="prev-print-numero">{p.numero}</p>
          <p>Stato: <b>{STATI_PREVENTIVO.find((x) => x.value === p.stato)?.label}</b></p>
        </PrintHeader>
        <div className="mt-4 grid grid-cols-3 gap-3">
          <Box title="Cliente"><p className="font-bold">{c.nome} {c.cognome}</p><p>{c.telefono}</p><p>{c.email}</p><p>{c.indirizzo}</p></Box>
          <Box title="Bici"><p className="font-bold">{b.marca} {b.modello}</p><p>{[b.tipo, b.colore, b.taglia].filter(Boolean).join(" · ")}</p><p className="font-mono">Telaio: {b.telaio || "—"}</p></Box>
          <Box title="Date"><p>Data: <b>{dataIt(p.data)}</b></p><p>Valido fino al: <b data-testid="prev-print-scadenza">{dataIt(scadenza(p))}</b></p></Box>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <Box title="Problema riscontrato"><p className="whitespace-pre-wrap">{p.problema || "—"}</p></Box>
          <Box title="Lavori previsti"><p className="whitespace-pre-wrap">{p.lavori_previsti || "—"}</p></Box>
        </div>
        {p.ricambi.length > 0 && (
          <table className="mt-5 w-full">
            <thead><tr><Th>Ricambio</Th><Th>Codice</Th><Th right>Qtà</Th><Th right>Prezzo</Th><Th right>Totale</Th></tr></thead>
            <tbody>{p.ricambi.map((r, i) => <tr key={i}><Td>{r.nome}</Td><Td mono>{r.codice}</Td><Td right>{r.quantita}</Td><Td right mono>{euro(r.prezzo)}</Td><Td right mono>{euro(r.quantita * r.prezzo)}</Td></tr>)}</tbody>
          </table>
        )}
        {p.manodopera.length > 0 && (
          <table className="mt-5 w-full">
            <thead><tr><Th>Manodopera</Th><Th right>Ore</Th><Th right>€/ora</Th><Th right>Totale</Th></tr></thead>
            <tbody>{p.manodopera.map((m, i) => <tr key={i}><Td>{m.descrizione}</Td><Td right>{m.fisso ? "a corpo" : m.ore}</Td><Td right mono>{euro(m.tariffa)}</Td><Td right mono>{euro(m.ore * m.tariffa)}</Td></tr>)}</tbody>
          </table>
        )}
        <div className="ml-auto mt-5 w-64 text-[13px]">
          <div className="flex justify-between py-1"><span>Totale ricambi</span><span className="font-mono">{euro(p.totale_ricambi)}</span></div>
          <div className="flex justify-between py-1"><span>Totale manodopera</span><span className="font-mono">{euro(p.totale_manodopera)}</span></div>
          <div className="flex justify-between border-t-2 border-slate-900 py-2 text-base font-bold"><span>TOTALE (IVA incl.)</span><span className="font-mono" data-testid="prev-print-totale">{euro(p.totale)}</span></div>
        </div>
        {p.note && <p className="mt-4 whitespace-pre-wrap text-[11px] text-slate-600">Note: {p.note}</p>}
        <p className="mt-6 text-[10px] text-slate-500">Il presente preventivo ha validità fino alla data indicata. Eventuali lavori o ricambi aggiuntivi emersi durante la riparazione verranno concordati con il cliente prima dell'esecuzione. Il cliente dichiara di aver ricevuto l'informativa sul trattamento dei dati personali ai sensi del Reg. UE 2016/679 (GDPR).</p>
        <div className="mt-6 grid grid-cols-2 items-end gap-16 text-[11px] text-slate-500">
          <div className="border-t border-slate-400 pt-1">Firma del negozio</div>
          <FirmaCliente label="Firma del cliente per accettazione del preventivo" endpoint={`/preventivi/${id}/firma`} doc={p} onSaved={load} prefix="preventivo" readOnly={!!pubblico} />
        </div>
        <PrintFooter text={neg.piede_preventivo} testId="prev-print-piede" />
      </div>
    </div>
  );
}
